import { CommonModule } from "@angular/common";
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { LucideDynamicIcon } from "@lucide/angular";
import { Subscription } from "rxjs";
import { backend } from "@/integrations/backend/client";
import { AuthService, Profile, User } from "../core/auth.service";
import { LanguageService } from "../core/language.service";
import { ToastService } from "../core/toast.service";
import { buttonClasses, inputClasses } from "./ui";

const CHAT_PRESENCE_TABLE = "chat_presence";
const CHAT_MESSAGES_TABLE = "chat_messages";
const CHAT_LOCAL_MESSAGES_KEY = "lina:chat:direct-messages";
const CHAT_AVAILABILITY_KEY_PREFIX = "lina:chat:available:";
const ONLINE_WINDOW_MS = 75_000;
const PRESENCE_HEARTBEAT_MS = 20_000;
const MESSAGE_POLL_MS = 1_500;
const PRESENCE_POLL_MS = 5_000;
const EXPIRED_CLEANUP_MS = 60_000;
const MESSAGE_LIMIT = 60;
const PARTICIPANT_LIMIT = 200;
const MESSAGE_TTL_MS = 24 * 60 * 60 * 1000;
const CHAT_PANEL_MARGIN_PX = 12;
const COMPACT_CHAT_BREAKPOINT_PX = 640;

interface ChatPresence {
  user_id: string;
  updated_at: string;
}

interface ChatMessage {
  id: string;
  user_id: string;
  recipient_user_id: string | null;
  body: string;
  created_at: string;
  expires_at: string | null;
}

interface ChatParticipant {
  user_id: string;
  display_name: string;
  avatar_url: string | null;
  hub: string | null;
  status_message: string | null;
  is_current_user: boolean;
  is_online: boolean;
  last_seen_at: string | null;
}

interface ChatPanelDragState {
  offsetX: number;
  offsetY: number;
  width: number;
  height: number;
}

interface ChatLauncherDragState extends ChatPanelDragState {
  startClientX: number;
  startClientY: number;
  hasMoved: boolean;
}

function errorText(error: unknown) {
  const backendError = error as {
    code?: string;
    message?: string;
    details?: string;
    hint?: string;
  } | null;

  return [
    backendError?.code,
    backendError?.message,
    backendError?.details,
    backendError?.hint,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function isChatPersistenceError(error: unknown) {
  const text = errorText(error);
  return (
    text.includes("42p01") ||
    text.includes("42703") ||
    text.includes("pgrst205") ||
    text.includes("42501") ||
    text.includes(CHAT_PRESENCE_TABLE) ||
    text.includes(CHAT_MESSAGES_TABLE) ||
    text.includes("schema cache") ||
    text.includes("row-level security") ||
    text.includes("permission denied")
  );
}

function localId() {
  return crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function localDisplayName(user: User | null, profile: Profile | null) {
  return (
    profile?.display_name?.trim() || user?.email?.split("@")[0] || "Explorer"
  );
}

@Component({
  selector: "app-chat-widget",
  standalone: true,
  imports: [CommonModule, FormsModule, LucideDynamicIcon],
  template: `
    <ng-container *ngIf="user">
      <button
        type="button"
        data-chat-launcher="true"
        data-map-ui-control="true"
        class="group fixed z-[90] grid h-10 w-11 cursor-pointer touch-none place-items-center rounded-xl bg-[#2f7de1] text-white shadow-xl ring-2 ring-[#2f7de1]/20 transition hover:scale-105 hover:bg-[#236fce] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-ring sm:h-12 sm:w-14 sm:cursor-move sm:rounded-[1rem] sm:shadow-2xl sm:ring-4"
        [ngStyle]="chatLauncherStyle"
        (click)="toggleOpen($event)"
        (pointerdown)="handleChatLauncherDragStart($event)"
        (document:pointermove)="handleChatLauncherDragMove($event)"
        (document:pointerup)="handleChatLauncherDragEnd($event)"
        (document:pointercancel)="handleChatLauncherDragEnd($event)"
        [attr.aria-label]="language.t('chat.open')"
        [title]="language.t('chat.open')"
      >
        <span
          class="absolute -bottom-1.5 left-3.5 hidden h-5 w-5 -rotate-45 rounded-bl-md bg-[#2f7de1] transition-colors group-hover:bg-[#236fce] sm:block"
          aria-hidden="true"
        ></span>
        <span
          class="relative flex items-center gap-1.5"
          aria-hidden="true"
        >
          <span class="h-2.5 w-2.5 rounded-full bg-white/90"></span>
          <span class="h-2.5 w-2.5 rounded-full bg-white/90"></span>
          <span class="h-2.5 w-2.5 rounded-full bg-white/90"></span>
        </span>
        <span
          *ngIf="unreadCount > 0"
          class="absolute -right-1 -top-1 grid h-6 min-w-6 place-items-center rounded-full bg-destructive px-1 text-xs font-bold text-destructive-foreground shadow"
        >
          {{ unreadCount }}
        </span>
      </button>

      <section
        *ngIf="open"
        data-chat-panel="true"
        class="fixed z-[95] flex h-[min(26rem,calc(100dvh-5rem))] w-[calc(100vw-1rem)] flex-col overflow-hidden rounded-lg border border-border bg-card text-card-foreground shadow-2xl sm:h-[min(37rem,calc(100vh-7rem))] sm:w-[min(36rem,calc(100vw-2rem))]"
        [ngStyle]="chatPanelStyle"
        (document:pointermove)="handleChatPanelDragMove($event)"
        (document:pointerup)="handleChatPanelDragEnd()"
        (document:pointercancel)="handleChatPanelDragEnd()"
      >
        <header
          class="grid h-14 shrink-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-2 border-b border-border bg-background/85 px-3 backdrop-blur sm:h-16 sm:px-4"
        >
          <div
            class="min-w-0 cursor-move touch-none select-none pr-1"
            [title]="language.t('chat.dragPanel')"
            (pointerdown)="handleChatPanelDragStart($event)"
          >
            <h2 class="truncate text-sm font-bold leading-tight sm:text-base">
              {{ language.t("chat.title") }}
            </h2>
            <p
              class="mt-0.5 hidden items-center gap-1.5 text-xs text-muted-foreground sm:flex"
            >
              <span class="h-2 w-2 rounded-full bg-accent"></span>
              {{
                language.t("chat.statusCount", {
                  online: onlineParticipants.length,
                  offline: offlineParticipants.length,
                })
              }}
            </p>
          </div>
          <div
            class="relative z-10 flex shrink-0 items-center justify-end gap-1"
            (pointerdown)="$event.stopPropagation()"
            (pointerup)="$event.stopPropagation()"
            (click)="$event.stopPropagation()"
          >
            <button
              type="button"
              class="inline-flex h-8 w-9 items-center justify-center gap-1.5 rounded-md border px-0 text-xs font-bold transition hover:bg-muted sm:h-9 sm:w-auto sm:px-2"
              [ngClass]="
                chatAvailable
                  ? 'border-accent/50 bg-accent/10 text-foreground'
                  : 'border-border bg-muted text-muted-foreground'
              "
              (click)="toggleChatAvailability($event)"
              [attr.aria-label]="
                language.t(chatAvailable ? 'chat.goOffline' : 'chat.goOnline')
              "
              [title]="
                language.t(chatAvailable ? 'chat.goOffline' : 'chat.goOnline')
              "
            >
              <span
                class="h-2.5 w-2.5 shrink-0 rounded-full"
                [ngClass]="
                  chatAvailable ? 'bg-accent' : 'bg-muted-foreground/50'
                "
                aria-hidden="true"
              ></span>
              <span class="hidden sm:inline">
                {{
                  language.t(
                    chatAvailable ? "chat.statusOnline" : "chat.statusOffline"
                  )
                }}
              </span>
            </button>
            <button
              type="button"
              [class]="buttonClasses('ghost', 'icon', 'hidden sm:inline-flex')"
              (click)="minimizeChat($event)"
              [attr.aria-label]="language.t('chat.minimize')"
              [title]="language.t('chat.minimize')"
            >
              <svg lucideIcon="minus" class="h-5 w-5"></svg>
            </button>
            <button
              type="button"
              class="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#2f7de1]/30 bg-[#2f7de1] text-xl font-black leading-none text-white shadow-md transition hover:bg-[#236fce] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-ring sm:h-10 sm:w-10 sm:rounded-md sm:border-border sm:bg-muted/70 sm:text-foreground sm:shadow-none sm:hover:bg-muted"
              (pointerdown)="$event.stopPropagation()"
              (pointerup)="closeChat($event)"
              (click)="closeChat($event)"
              [attr.aria-label]="language.t('chat.close')"
              [title]="language.t('chat.close')"
            >
              <span aria-hidden="true">X</span>
            </button>
          </div>
        </header>

        <div class="flex min-h-0 flex-1 flex-col sm:flex-row">
          <aside
            class="flex h-24 shrink-0 flex-col border-b border-border bg-background/45 sm:h-auto sm:w-40 sm:border-b-0 sm:border-r"
          >
            <div
              class="hidden h-12 shrink-0 grid-cols-2 border-b border-border text-center text-[11px] sm:grid"
            >
              <div class="grid place-items-center">
                <span class="font-bold text-accent">{{
                  onlineParticipants.length
                }}</span>
                <span class="text-muted-foreground">{{
                  language.t("chat.online")
                }}</span>
              </div>
              <div class="grid place-items-center border-l border-border">
                <span class="font-bold text-muted-foreground">{{
                  offlineParticipants.length
                }}</span>
                <span class="text-muted-foreground">{{
                  language.t("chat.offline")
                }}</span>
              </div>
            </div>

            <div
              class="flex min-h-0 flex-1 gap-2 overflow-x-auto overflow-y-hidden p-2 sm:block sm:overflow-y-auto"
            >
              <p
                class="mb-2 hidden px-1 text-[10px] font-bold uppercase text-muted-foreground sm:block"
              >
                {{ language.t("chat.online") }}
              </p>
              <button
                *ngFor="
                  let participant of onlineParticipants;
                  trackBy: trackByUserId
                "
                type="button"
                [class]="participantButtonClasses(participant)"
                (click)="selectParticipant(participant)"
                [title]="participant.display_name"
              >
                <span
                  class="relative grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary/10 text-xs font-bold text-primary"
                >
                  <img
                    *ngIf="participant.avatar_url"
                    [src]="participant.avatar_url"
                    [alt]="participant.display_name"
                    class="h-full w-full rounded-full object-cover"
                  />
                  <span *ngIf="!participant.avatar_url">
                    {{ initials(participant.display_name) }}
                  </span>
                  <span
                    class="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-card bg-accent"
                  ></span>
                </span>
                <span
                  class="min-w-0 flex-1 text-left max-sm:w-full max-sm:max-w-full max-sm:flex-none max-sm:overflow-hidden max-sm:text-center"
                >
                  <span
                    class="block truncate text-xs font-medium max-sm:w-full max-sm:max-w-full"
                  >
                    {{ participant.display_name }}
                  </span>
                  <span
                    *ngIf="participant.status_message"
                    class="mt-0.5 hidden truncate text-[10px] leading-tight opacity-75 sm:block"
                  >
                    {{ participant.status_message }}
                  </span>
                </span>
                <span
                  *ngIf="unreadFor(participant.user_id) > 0"
                  class="grid h-5 min-w-5 place-items-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground"
                >
                  {{ unreadFor(participant.user_id) }}
                </span>
              </button>

              <p
                class="mb-2 mt-3 hidden px-1 text-[10px] font-bold uppercase text-muted-foreground sm:block"
              >
                {{ language.t("chat.offline") }}
              </p>
              <button
                *ngFor="
                  let participant of offlineParticipants;
                  trackBy: trackByUserId
                "
                type="button"
                [class]="participantButtonClasses(participant)"
                (click)="selectParticipant(participant)"
                [title]="participant.display_name"
              >
                <span
                  class="relative grid h-9 w-9 shrink-0 place-items-center rounded-full bg-muted text-xs font-bold text-muted-foreground"
                >
                  <img
                    *ngIf="participant.avatar_url"
                    [src]="participant.avatar_url"
                    [alt]="participant.display_name"
                    class="h-full w-full rounded-full object-cover grayscale"
                  />
                  <span *ngIf="!participant.avatar_url">
                    {{ initials(participant.display_name) }}
                  </span>
                  <span
                    class="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-card bg-muted-foreground/40"
                  ></span>
                </span>
                <span
                  class="min-w-0 flex-1 text-left max-sm:w-full max-sm:max-w-full max-sm:flex-none max-sm:overflow-hidden max-sm:text-center"
                >
                  <span
                    class="block truncate text-xs font-medium max-sm:w-full max-sm:max-w-full"
                  >
                    {{ participant.display_name }}
                  </span>
                  <span
                    *ngIf="participant.status_message"
                    class="mt-0.5 hidden truncate text-[10px] leading-tight opacity-75 sm:block"
                  >
                    {{ participant.status_message }}
                  </span>
                </span>
                <span
                  *ngIf="unreadFor(participant.user_id) > 0"
                  class="grid h-5 min-w-5 place-items-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground"
                >
                  {{ unreadFor(participant.user_id) }}
                </span>
              </button>
            </div>
          </aside>

          <div class="flex min-w-0 flex-1 flex-col">
            <div
              class="flex h-12 shrink-0 items-center border-b border-border px-3 sm:h-16 sm:px-4"
            >
              <ng-container *ngIf="selectedParticipant; else noConversation">
                <div class="min-w-0">
                  <p class="truncate text-sm font-bold">
                    {{ selectedParticipant.display_name }}
                  </p>
                  <p
                    *ngIf="selectedParticipant.status_message"
                    class="truncate text-xs text-muted-foreground"
                  >
                    {{ selectedParticipant.status_message }}
                  </p>
                  <p
                    *ngIf="!selectedParticipant.status_message"
                    class="text-xs text-muted-foreground"
                  >
                    {{
                      selectedParticipant.is_online
                        ? language.t("chat.online")
                        : language.t("chat.offline")
                    }}
                  </p>
                </div>
              </ng-container>
              <ng-template #noConversation>
                <p class="text-sm font-medium text-muted-foreground">
                  {{ language.t("chat.selectPerson") }}
                </p>
              </ng-template>
            </div>

            <div
              *ngIf="localOnly"
              class="shrink-0 border-b border-secondary/30 bg-secondary/10 px-4 py-2 text-xs text-secondary-foreground"
            >
              {{ language.t("chat.localOnly") }}
            </div>

            <div
              #messageList
              class="min-h-0 flex-1 space-y-3 overflow-y-auto bg-background/45 px-3 py-3 sm:px-4 sm:py-4"
            >
              <p
                *ngIf="!selectedParticipant"
                class="mt-16 text-center text-sm text-muted-foreground"
              >
                {{ language.t("chat.selectPersonDescription") }}
              </p>

              <p
                *ngIf="selectedParticipant && messages.length === 0"
                class="mt-16 text-center text-sm text-muted-foreground"
              >
                {{ language.t("chat.emptyDirect") }}
              </p>

              <div
                *ngFor="let message of messages; trackBy: trackByMessageId"
                class="flex"
                [ngClass]="{
                  'justify-end': message.user_id === user.id,
                  'justify-start': message.user_id !== user.id,
                }"
              >
                <article
                  class="max-w-[82%] rounded-lg px-3 py-2 text-sm shadow-sm"
                  [ngClass]="{
                    'bg-primary text-primary-foreground':
                      message.user_id === user.id,
                    'border border-border bg-card text-card-foreground':
                      message.user_id !== user.id,
                  }"
                >
                  <p
                    class="mb-1 text-[11px] font-semibold opacity-75"
                    *ngIf="message.user_id !== user.id"
                  >
                    {{ participantName(message.user_id) }}
                  </p>
                  <p class="whitespace-pre-wrap break-words leading-relaxed">
                    {{ message.body }}
                  </p>
                  <time class="mt-1 block text-right text-[10px] opacity-65">
                    {{ messageTime(message.created_at) }}
                  </time>
                </article>
              </div>
            </div>

            <form
              class="shrink-0 border-t border-border bg-card p-2 sm:p-3"
              (ngSubmit)="sendMessage()"
            >
              <div class="flex items-center gap-2">
                <input
                  [class]="inputClasses('h-11 flex-1')"
                  name="chatMessage"
                  [(ngModel)]="draft"
                  maxlength="500"
                  autocomplete="off"
                  [placeholder]="
                    selectedParticipant
                      ? language.t('chat.placeholder')
                      : language.t('chat.selectFirst')
                  "
                  [disabled]="sending || !selectedParticipant"
                />
                <button
                  type="submit"
                  class="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[#2f7de1] text-white shadow-md transition hover:scale-105 hover:bg-[#236fce] disabled:cursor-not-allowed disabled:opacity-50"
                  [disabled]="
                    sending || !selectedParticipant || !draft.trim()
                  "
                  [attr.aria-label]="language.t('chat.send')"
                  [title]="language.t('chat.send')"
                >
                  <svg
                    *ngIf="!sending"
                    viewBox="0 0 24 24"
                    class="h-5 w-5"
                    aria-hidden="true"
                  >
                    <path
                      fill="currentColor"
                      d="M21.86 2.64a1.02 1.02 0 0 0-1.1-.18L2.83 10.02a1 1 0 0 0 .08 1.87l6.6 2.16 2.16 6.6a1 1 0 0 0 .89.69h.06a1 1 0 0 0 .9-.56l8.47-17.05a1 1 0 0 0-.13-1.09ZM10.4 12.2l-4.32-1.41 10.88-4.58-6.56 5.99Zm2.81 5.72-1.41-4.32 5.99-6.56-4.58 10.88Z"
                    ></path>
                  </svg>
                  <svg
                    *ngIf="sending"
                    lucideIcon="loader-circle"
                    class="h-5 w-5 animate-spin"
                  ></svg>
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </ng-container>
  `,
})
export class ChatWidgetComponent implements OnInit, OnDestroy {
  readonly language = inject(LanguageService);
  private readonly auth = inject(AuthService);
  private readonly toast = inject(ToastService);
  private readonly zone = inject(NgZone);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly subscriptions = new Subscription();
  private presenceTimer?: number;
  private messageTimer?: number;
  private rosterTimer?: number;
  private profile: Profile | null = null;
  private hasLoadedMessages = false;
  private hasLoadedIncomingMessages = false;
  private hasLoadedPresence = false;
  private refreshingMessages = false;
  private refreshingIncomingMessages = false;
  private refreshingPresence = false;
  private messageRefreshGeneration = 0;
  private lastExpiredCleanupAt = 0;
  private readonly knownIncomingMessageIds = new Set<string>();
  private readonly unreadByUserId = new Map<string, number>();
  private previousOnlineUserIds = new Set<string>();
  private chatPanelDragState: ChatPanelDragState | null = null;
  private chatPanelHasCustomPosition = false;
  private chatLauncherDragState: ChatLauncherDragState | null = null;
  private chatLauncherHasCustomPosition = false;
  private suppressNextLauncherClick = false;

  @ViewChild("messageList") private messageList?: ElementRef<HTMLDivElement>;

  user: User | null = null;
  open = false;
  localOnly = false;
  sending = false;
  draft = "";
  selectedParticipantId: string | null = null;
  unreadCount = 0;
  messages: ChatMessage[] = [];
  onlineParticipants: ChatParticipant[] = [];
  offlineParticipants: ChatParticipant[] = [];
  participantsById = new Map<string, ChatParticipant>();
  chatPanelPosition = { x: 0, y: 0 };
  chatLauncherPosition = { x: 0, y: 0 };
  chatAvailable = true;

  private readonly refreshOnFocus = () => {
    if (document.visibilityState === "hidden") return;
    this.zone.run(() => void this.refreshChat());
  };

  get selectedParticipant() {
    return this.selectedParticipantId
      ? this.participantsById.get(this.selectedParticipantId) || null
      : null;
  }

  get chatPanelStyle(): Record<string, string> {
    if (this.isCompactChatViewport()) {
      return {
        left: "0.5rem",
        right: "0.5rem",
        bottom: "0.5rem",
        top: "auto",
      };
    }

    if (!this.chatPanelHasCustomPosition) {
      return {
        left: "1rem",
        bottom: "5rem",
      };
    }

    return {
      left: `${this.chatPanelPosition.x}px`,
      top: `${this.chatPanelPosition.y}px`,
    };
  }

  get chatLauncherStyle(): Record<string, string> {
    if (this.isCompactChatViewport()) {
      return {
        right: "0.75rem",
        bottom: "0.75rem",
      };
    }

    if (!this.chatLauncherHasCustomPosition) {
      return {
        left: "1rem",
        bottom: "1rem",
      };
    }

    return {
      left: `${this.chatLauncherPosition.x}px`,
      top: `${this.chatLauncherPosition.y}px`,
    };
  }

  ngOnInit() {
    window.addEventListener("focus", this.refreshOnFocus);
    document.addEventListener("visibilitychange", this.refreshOnFocus);

    this.subscriptions.add(
      this.auth.state$.subscribe((state) => {
        const previousUserId = this.user?.id ?? null;
        this.user = state.user;
        this.profile = state.profile;

        if (!this.user) {
          this.resetChatState();
          return;
        }

        this.chatAvailable = this.readChatAvailability();
        this.ensureCurrentUserOnline();
        if (previousUserId !== this.user.id) {
          this.localOnly = false;
          this.selectedParticipantId = null;
          this.unreadCount = 0;
          this.unreadByUserId.clear();
          this.knownIncomingMessageIds.clear();
          this.previousOnlineUserIds = new Set();
          this.hasLoadedMessages = false;
          this.hasLoadedIncomingMessages = false;
          this.hasLoadedPresence = false;
          if (!this.chatAvailable) {
            void this.clearRemotePresence();
          }
          void this.refreshChat();
          this.startTimers();
        }
        this.markView();
      }),
    );
  }

  ngOnDestroy() {
    window.removeEventListener("focus", this.refreshOnFocus);
    document.removeEventListener("visibilitychange", this.refreshOnFocus);
    this.stopTimers();
    this.subscriptions.unsubscribe();
  }

  toggleOpen(event?: Event) {
    event?.stopPropagation();
    if (this.suppressNextLauncherClick) {
      this.suppressNextLauncherClick = false;
      return;
    }

    this.open = !this.open;
    this.chatLauncherDragState = null;
    if (this.open) {
      if (this.selectedParticipantId) {
        this.clearUnreadFor(this.selectedParticipantId);
      }
      void this.refreshChat();
      this.scrollToBottomSoon();
    }
  }

  minimizeChat(event?: Event) {
    event?.stopPropagation();
    this.open = false;
    this.chatPanelDragState = null;
    this.chatLauncherDragState = null;
    this.markView();
  }

  closeChat(event?: Event) {
    event?.preventDefault();
    event?.stopPropagation();
    this.open = false;
    this.chatPanelDragState = null;
    this.chatLauncherDragState = null;
    this.selectedParticipantId = null;
    this.messages = [];
    this.draft = "";
    this.hasLoadedMessages = false;
    this.markView();
  }

  async toggleChatAvailability(event?: Event) {
    event?.stopPropagation();
    if (!this.user) return;

    this.chatAvailable = !this.chatAvailable;
    this.writeChatAvailability();
    this.ensureCurrentUserOnline();

    if (this.chatAvailable) {
      await this.updatePresence();
    } else {
      await this.clearRemotePresence();
    }

    await this.refreshPresence();
    this.markView();
  }

  handleChatLauncherDragStart(event: PointerEvent) {
    if (this.isCompactChatViewport()) return;
    if (event.pointerType === "mouse" && event.button !== 0) return;

    const launcher = (event.currentTarget as HTMLElement).closest(
      "[data-chat-launcher]",
    ) as HTMLElement | null;
    if (!launcher) return;

    const rect = launcher.getBoundingClientRect();
    const position = this.constrainChatFloatingPosition(
      rect.left,
      rect.top,
      rect.width,
      rect.height,
    );

    this.chatLauncherHasCustomPosition = true;
    this.chatLauncherPosition = position;
    this.chatLauncherDragState = {
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
      width: rect.width,
      height: rect.height,
      startClientX: event.clientX,
      startClientY: event.clientY,
      hasMoved: false,
    };

    event.stopPropagation();
  }

  handleChatLauncherDragMove(event: PointerEvent) {
    if (!this.chatLauncherDragState) return;

    const distance = Math.hypot(
      event.clientX - this.chatLauncherDragState.startClientX,
      event.clientY - this.chatLauncherDragState.startClientY,
    );
    if (distance > 4) {
      this.chatLauncherDragState.hasMoved = true;
    }

    const position = this.constrainChatFloatingPosition(
      event.clientX - this.chatLauncherDragState.offsetX,
      event.clientY - this.chatLauncherDragState.offsetY,
      this.chatLauncherDragState.width,
      this.chatLauncherDragState.height,
    );

    this.chatLauncherPosition = position;
    event.preventDefault();
    event.stopPropagation();
    this.markView();
  }

  handleChatLauncherDragEnd(event?: PointerEvent) {
    if (this.chatLauncherDragState?.hasMoved) {
      this.suppressNextLauncherClick = true;
    }
    this.chatLauncherDragState = null;
    event?.stopPropagation();
    this.markView();
  }

  handleChatPanelDragStart(event: PointerEvent) {
    if (this.isCompactChatViewport()) return;
    if (event.pointerType === "mouse" && event.button !== 0) return;

    const panel = (event.currentTarget as HTMLElement).closest(
      "[data-chat-panel]",
    ) as HTMLElement | null;
    if (!panel) return;

    const rect = panel.getBoundingClientRect();
    const position = this.constrainChatFloatingPosition(
      rect.left,
      rect.top,
      rect.width,
      rect.height,
    );

    this.chatPanelHasCustomPosition = true;
    this.chatPanelPosition = position;
    this.chatPanelDragState = {
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
      width: rect.width,
      height: rect.height,
    };

    event.preventDefault();
    event.stopPropagation();
  }

  handleChatPanelDragMove(event: PointerEvent) {
    if (!this.chatPanelDragState) return;

    const position = this.constrainChatFloatingPosition(
      event.clientX - this.chatPanelDragState.offsetX,
      event.clientY - this.chatPanelDragState.offsetY,
      this.chatPanelDragState.width,
      this.chatPanelDragState.height,
    );

    this.chatPanelPosition = position;
    event.preventDefault();
    event.stopPropagation();
  }

  handleChatPanelDragEnd() {
    this.chatPanelDragState = null;
  }

  private constrainChatFloatingPosition(
    left: number,
    top: number,
    width: number,
    height: number,
  ) {
    const maxLeft = Math.max(
      CHAT_PANEL_MARGIN_PX,
      window.innerWidth - width - CHAT_PANEL_MARGIN_PX,
    );
    const maxTop = Math.max(
      CHAT_PANEL_MARGIN_PX,
      window.innerHeight - height - CHAT_PANEL_MARGIN_PX,
    );

    return {
      x: Math.min(Math.max(left, CHAT_PANEL_MARGIN_PX), maxLeft),
      y: Math.min(Math.max(top, CHAT_PANEL_MARGIN_PX), maxTop),
    };
  }

  selectParticipant(participant: ChatParticipant) {
    if (participant.is_current_user) return;

    this.selectedParticipantId = participant.user_id;
    this.messages = [];
    this.hasLoadedMessages = false;
    this.clearUnreadFor(participant.user_id);
    void this.refreshMessages(true);
    this.scrollToBottomSoon();
  }

  initials(name: string) {
    return name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? "")
      .join("");
  }

  participantName(userId: string) {
    return this.participantsById.get(userId)?.display_name || "Explorer";
  }

  messageTime(value: string) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";

    return new Intl.DateTimeFormat(undefined, {
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  }

  participantButtonClasses(participant: ChatParticipant) {
    const selected = participant.user_id === this.selectedParticipantId;
    return [
      "mb-1 flex min-h-12 w-full items-center gap-2 overflow-hidden rounded-md px-2 py-1.5 text-left transition max-sm:mb-0 max-sm:min-h-16 max-sm:w-24 max-sm:shrink-0 max-sm:flex-col max-sm:justify-center max-sm:gap-1 max-sm:px-1 max-sm:text-center",
      selected
        ? "bg-primary text-primary-foreground shadow-sm"
        : "text-foreground hover:bg-muted",
    ].join(" ");
  }

  unreadFor(userId: string) {
    return this.unreadByUserId.get(userId) || 0;
  }

  trackByUserId(_index: number, participant: ChatParticipant) {
    return participant.user_id;
  }

  trackByMessageId(_index: number, message: ChatMessage) {
    return message.id;
  }

  async sendMessage() {
    const body = this.draft.trim();
    const recipientId = this.selectedParticipantId;
    if (!this.user || !recipientId || !body) return;

    this.sending = true;
    try {
      if (this.localOnly) {
        this.saveLocalMessage(body, recipientId);
      } else {
        const { error } = await backend.from(CHAT_MESSAGES_TABLE).insert({
          user_id: this.user.id,
          recipient_user_id: recipientId,
          body,
        });

        if (error) throw error;
        await this.refreshMessages(true);
      }

      this.draft = "";
      this.scrollToBottomSoon();
      this.markView();
    } catch (error) {
      console.warn("Direct chat message could not be sent remotely:", error);
      if (!isChatPersistenceError(error)) {
        this.toast.error(this.language.t("chat.sendError"));
        return;
      }

      this.localOnly = true;
      this.saveLocalMessage(body, recipientId);
      this.draft = "";
    } finally {
      this.sending = false;
      this.markView();
    }
  }

  private resetChatState() {
    this.stopTimers();
    this.open = false;
    this.messages = [];
    this.onlineParticipants = [];
    this.offlineParticipants = [];
    this.participantsById = new Map();
    this.selectedParticipantId = null;
    this.chatAvailable = true;
    this.unreadCount = 0;
    this.unreadByUserId.clear();
    this.knownIncomingMessageIds.clear();
    this.previousOnlineUserIds = new Set();
    this.markView();
  }

  private startTimers() {
    this.stopTimers();
    this.presenceTimer = window.setInterval(() => {
      this.zone.run(() => void this.updatePresence());
    }, PRESENCE_HEARTBEAT_MS);
    this.messageTimer = window.setInterval(() => {
      this.zone.run(() => void this.refreshMessageData());
    }, MESSAGE_POLL_MS);
    this.rosterTimer = window.setInterval(() => {
      this.zone.run(() => void this.refreshPresence());
    }, PRESENCE_POLL_MS);
  }

  private stopTimers() {
    if (this.presenceTimer) window.clearInterval(this.presenceTimer);
    if (this.messageTimer) window.clearInterval(this.messageTimer);
    if (this.rosterTimer) window.clearInterval(this.rosterTimer);
    this.presenceTimer = undefined;
    this.messageTimer = undefined;
    this.rosterTimer = undefined;
  }

  private async refreshChat() {
    if (!this.user) return;

    await this.updatePresence();
    await Promise.all([this.refreshPresence(), this.refreshMessageData()]);
    this.markView();
  }

  private async refreshMessageData() {
    await this.deleteExpiredMessages();
    await Promise.all([this.refreshIncomingMessages(), this.refreshMessages()]);
  }

  private async updatePresence() {
    if (!this.user) return;

    if (!this.chatAvailable) {
      this.ensureCurrentUserOnline();
      return;
    }

    if (this.localOnly) {
      this.ensureCurrentUserOnline();
      return;
    }

    try {
      const { error } = await backend.from(CHAT_PRESENCE_TABLE).upsert(
        {
          user_id: this.user.id,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "user_id" },
      );

      if (error) throw error;
    } catch (error) {
      console.warn("Chat presence could not be updated remotely:", error);
      if (isChatPersistenceError(error)) {
        this.localOnly = true;
        this.ensureCurrentUserOnline();
      }
    } finally {
      this.markView();
    }
  }

  private async clearRemotePresence() {
    if (!this.user || this.localOnly) return;

    try {
      const { error } = await backend
        .from(CHAT_PRESENCE_TABLE)
        .delete()
        .eq("user_id", this.user.id);

      if (error) throw error;
    } catch (error) {
      console.warn("Chat presence could not be cleared remotely:", error);
      if (isChatPersistenceError(error)) {
        this.localOnly = true;
      }
    } finally {
      this.ensureCurrentUserOnline();
      this.markView();
    }
  }

  private async refreshPresence() {
    if (!this.user || this.localOnly) {
      this.ensureCurrentUserOnline();
      return;
    }

    if (this.refreshingPresence) return;
    this.refreshingPresence = true;

    try {
      const onlineSince = Date.now() - ONLINE_WINDOW_MS;
      const { data, error } = await backend
        .from(CHAT_PRESENCE_TABLE)
        .select("user_id, updated_at")
        .order("updated_at", { ascending: false })
        .limit(PARTICIPANT_LIMIT);

      if (error) throw error;

      const presences = ((data || []) as ChatPresence[]).filter((presence) =>
        Boolean(presence.user_id),
      );
      const lastSeenById = new Map<string, string>();
      const onlineIds = new Set<string>();
      if (this.chatAvailable) {
        onlineIds.add(this.user.id);
      }

      presences.forEach((presence) => {
        lastSeenById.set(presence.user_id, presence.updated_at);
        if (
          new Date(presence.updated_at).getTime() >= onlineSince &&
          (presence.user_id !== this.user?.id || this.chatAvailable)
        ) {
          onlineIds.add(presence.user_id);
        }
      });

      const directoryIds = await this.loadParticipantDirectory();
      const userIds = new Set([
        this.user.id,
        ...directoryIds,
        ...presences.map((presence) => presence.user_id),
        ...this.messages.flatMap((message) => [
          message.user_id,
          message.recipient_user_id || "",
        ]),
      ]);

      await this.loadParticipants(Array.from(userIds));
      this.applyParticipantPresence(userIds, onlineIds, lastSeenById);
      this.notifyOnlineTransitions(onlineIds);
      this.previousOnlineUserIds = onlineIds;
      this.hasLoadedPresence = true;
    } catch (error) {
      console.warn("Chat presence could not be loaded remotely:", error);
      if (isChatPersistenceError(error)) {
        this.localOnly = true;
        this.ensureCurrentUserOnline();
      }
    } finally {
      this.refreshingPresence = false;
      this.markView();
    }
  }

  private async refreshMessages(force = false) {
    if (!this.user || !this.selectedParticipantId) {
      this.messages = [];
      return;
    }
    if (this.refreshingMessages && !force) return;

    this.refreshingMessages = true;
    const refreshGeneration = ++this.messageRefreshGeneration;
    const selectedParticipantId = this.selectedParticipantId;

    if (this.localOnly) {
      this.messages = this.readLocalMessages().filter((message) =>
        this.isConversationMessage(message, selectedParticipantId),
      );
      this.ensureMessageParticipants();
      this.scrollToBottomSoon();
      this.refreshingMessages = false;
      this.markView();
      return;
    }

    try {
      const participantIds = [this.user.id, selectedParticipantId];
      const { data, error } = await backend
        .from(CHAT_MESSAGES_TABLE)
        .select("id, user_id, recipient_user_id, body, created_at, expires_at")
        .in("user_id", participantIds)
        .in("recipient_user_id", participantIds)
        .order("created_at", { ascending: false })
        .limit(MESSAGE_LIMIT);

      if (error) throw error;
      if (refreshGeneration !== this.messageRefreshGeneration) return;

      const nextMessages = ((data || []) as ChatMessage[])
        .filter((message) =>
          this.isConversationMessage(message, selectedParticipantId),
        )
        .sort(
          (left, right) =>
            new Date(left.created_at).getTime() -
            new Date(right.created_at).getTime(),
        );

      this.messages = nextMessages;
      await this.loadParticipants(
        Array.from(
          new Set(
            nextMessages.flatMap((message) => [
              message.user_id,
              message.recipient_user_id || "",
            ]),
          ),
        ),
      );

      if (this.open) {
        this.scrollToBottomSoon();
      }
      this.hasLoadedMessages = true;
      this.refreshLocalRosterFromMessages();
    } catch (error) {
      console.warn("Direct chat messages could not be loaded remotely:", error);
      if (isChatPersistenceError(error)) {
        this.localOnly = true;
        this.messages = this.readLocalMessages().filter((message) =>
          this.isConversationMessage(message, selectedParticipantId),
        );
        this.ensureMessageParticipants();
      }
    } finally {
      this.refreshingMessages = false;
      this.markView();
    }
  }

  private async refreshIncomingMessages() {
    if (!this.user || this.localOnly || this.refreshingIncomingMessages) return;
    this.refreshingIncomingMessages = true;

    try {
      const { data, error } = await backend
        .from(CHAT_MESSAGES_TABLE)
        .select("id, user_id, recipient_user_id, body, created_at, expires_at")
        .eq("recipient_user_id", this.user.id)
        .order("created_at", { ascending: false })
        .limit(MESSAGE_LIMIT);

      if (error) throw error;

      const incomingMessages = ((data || []) as ChatMessage[])
        .filter((message) => this.isVisibleDirectMessage(message))
        .sort(
          (left, right) =>
            new Date(left.created_at).getTime() -
            new Date(right.created_at).getTime(),
        );

      await this.loadParticipants(
        Array.from(new Set(incomingMessages.map((message) => message.user_id))),
      );

      const newMessages = this.hasLoadedIncomingMessages
        ? incomingMessages.filter(
            (message) => !this.knownIncomingMessageIds.has(message.id),
          )
        : [];

      incomingMessages.forEach((message) =>
        this.knownIncomingMessageIds.add(message.id),
      );

      if (newMessages.length > 0) {
        this.notifyNewMessages(newMessages);
        this.applyIncomingUnread(newMessages);
        if (
          this.open &&
          this.selectedParticipantId &&
          newMessages.some(
            (message) => message.user_id === this.selectedParticipantId,
          )
        ) {
          await this.refreshMessages(true);
        }
      }

      this.hasLoadedIncomingMessages = true;
    } catch (error) {
      console.warn("Incoming direct messages could not be loaded:", error);
      if (isChatPersistenceError(error)) {
        this.localOnly = true;
      }
    } finally {
      this.refreshingIncomingMessages = false;
      this.markView();
    }
  }

  private async deleteExpiredMessages() {
    if (!this.user || this.localOnly) return;

    const now = Date.now();
    if (now - this.lastExpiredCleanupAt < EXPIRED_CLEANUP_MS) return;
    this.lastExpiredCleanupAt = now;

    try {
      const { error } = await backend
        .from(CHAT_MESSAGES_TABLE)
        .delete()
        .filter("expires_at", "lt", new Date().toISOString());

      if (error && !isChatPersistenceError(error)) {
        console.warn("Expired direct chat messages could not be deleted:", error);
      }
    } catch (error) {
      console.warn("Expired direct chat cleanup failed:", error);
    }
  }

  private async loadParticipants(userIds: string[]) {
    if (!this.user) return;
    const normalizedIds = Array.from(new Set(userIds.filter(Boolean)));
    if (normalizedIds.length === 0) return;

    const missingIds = normalizedIds.filter(
      (id) => !this.participantsById.has(id),
    );
    if (missingIds.length === 0) return;

    try {
      const { data, error } = await backend
        .from("profiles")
        .select("id, display_name, avatar_url, hub, profile_status_message")
        .in("id", missingIds);

      if (error) throw error;

      const nextParticipants = new Map(this.participantsById);
      ((data || []) as Profile[]).forEach((profile) => {
        nextParticipants.set(
          profile.id,
          this.participantFromProfile(
            profile,
            nextParticipants.get(profile.id),
          ),
        );
      });

      if (this.user && missingIds.includes(this.user.id)) {
        nextParticipants.set(this.user.id, this.currentUserParticipant());
      }

      this.participantsById = nextParticipants;
    } catch (error) {
      console.warn("Chat profiles could not be loaded:", error);
      this.ensureCurrentUserOnline();
    }
  }

  private async loadParticipantDirectory() {
    try {
      const { data, error } = await backend
        .from("profiles")
        .select("id, display_name, avatar_url, hub, profile_status_message")
        .order("display_name", { ascending: true })
        .limit(PARTICIPANT_LIMIT);

      if (error) throw error;

      const nextParticipants = new Map(this.participantsById);
      const ids: string[] = [];

      ((data || []) as Profile[]).forEach((profile) => {
        if (!profile.id) return;
        ids.push(profile.id);
        nextParticipants.set(
          profile.id,
          this.participantFromProfile(
            profile,
            nextParticipants.get(profile.id),
          ),
        );
      });

      if (this.user) {
        nextParticipants.set(this.user.id, this.currentUserParticipant());
      }

      this.participantsById = nextParticipants;
      return ids;
    } catch (error) {
      console.warn("Chat participant directory could not be loaded:", error);
      return [];
    }
  }

  private ensureCurrentUserOnline() {
    if (!this.user) return;

    const currentUser = this.currentUserParticipant();
    const nextParticipants = new Map(this.participantsById);
    nextParticipants.set(this.user.id, currentUser);
    this.participantsById = nextParticipants;
    this.rebuildRoster([
      ...this.onlineParticipants.map((participant) => participant.user_id),
      ...this.offlineParticipants.map((participant) => participant.user_id),
      this.user.id,
    ]);
  }

  private ensureMessageParticipants() {
    this.ensureCurrentUserOnline();
    const nextParticipants = new Map(this.participantsById);
    this.messages.forEach((message) => {
      [message.user_id, message.recipient_user_id].forEach((userId) => {
        if (!userId || nextParticipants.has(userId)) return;
        nextParticipants.set(userId, this.fallbackParticipant(userId));
      });
    });
    this.participantsById = nextParticipants;
    this.refreshLocalRosterFromMessages();
  }

  private currentUserParticipant(): ChatParticipant {
    const previous = this.participantsById.get(this.user!.id);

    return {
      user_id: this.user!.id,
      display_name: localDisplayName(this.user, this.profile),
      avatar_url: this.profile?.avatar_url ?? null,
      hub: this.profile?.hub ?? null,
      status_message: this.profile?.profile_status_message?.trim() || null,
      is_current_user: true,
      is_online: this.chatAvailable,
      last_seen_at: previous?.last_seen_at ?? new Date().toISOString(),
    };
  }

  private participantFromProfile(
    profile: Profile,
    previous?: ChatParticipant,
  ): ChatParticipant {
    return {
      user_id: profile.id,
      display_name: profile.display_name || previous?.display_name || "Explorer",
      avatar_url: profile.avatar_url ?? previous?.avatar_url ?? null,
      hub: profile.hub ?? previous?.hub ?? null,
      status_message:
        profile.profile_status_message?.trim() ||
        previous?.status_message ||
        null,
      is_current_user: profile.id === this.user?.id,
      is_online: previous?.is_online ?? false,
      last_seen_at: previous?.last_seen_at ?? null,
    };
  }

  private fallbackParticipant(userId: string): ChatParticipant {
    return {
      user_id: userId,
      display_name: "Explorer",
      avatar_url: null,
      hub: null,
      status_message: null,
      is_current_user: userId === this.user?.id,
      is_online: userId === this.user?.id ? this.chatAvailable : false,
      last_seen_at: null,
    };
  }

  private applyParticipantPresence(
    userIds: Iterable<string>,
    onlineIds: Set<string>,
    lastSeenById: Map<string, string>,
  ) {
    if (!this.user) return;

    const visibleIds = new Set(userIds);
    visibleIds.add(this.user.id);

    const nextParticipants = new Map(this.participantsById);
    visibleIds.forEach((userId) => {
      if (!userId) return;
      const previous =
        nextParticipants.get(userId) ?? this.fallbackParticipant(userId);
      const isOnline = onlineIds.has(userId);

      nextParticipants.set(userId, {
        ...previous,
        is_current_user: userId === this.user?.id,
        is_online: isOnline,
        last_seen_at: lastSeenById.get(userId) ?? previous.last_seen_at,
      });
    });

    this.participantsById = nextParticipants;
    this.rebuildRoster(Array.from(visibleIds));
  }

  private refreshLocalRosterFromMessages() {
    if (!this.user) return;

    const visibleIds = new Set([
      this.user.id,
      ...this.onlineParticipants.map((participant) => participant.user_id),
      ...this.offlineParticipants.map((participant) => participant.user_id),
      ...this.messages.flatMap((message) => [
        message.user_id,
        message.recipient_user_id || "",
      ]),
    ]);

    this.rebuildRoster(Array.from(visibleIds));
  }

  private rebuildRoster(userIds: string[]) {
    const participants = userIds
      .filter((userId) => userId && userId !== this.user?.id)
      .map(
        (userId) =>
          this.participantsById.get(userId) ?? this.fallbackParticipant(userId),
      )
      .sort((left, right) => this.compareParticipants(left, right));

    this.onlineParticipants = participants.filter(
      (participant) => participant.is_online,
    );
    this.offlineParticipants = participants.filter(
      (participant) => !participant.is_online,
    );
  }

  private compareParticipants(
    left: ChatParticipant,
    right: ChatParticipant,
  ) {
    if (left.is_current_user) return -1;
    if (right.is_current_user) return 1;
    if (left.is_online !== right.is_online) return left.is_online ? -1 : 1;
    return left.display_name.localeCompare(right.display_name);
  }

  private notifyOnlineTransitions(onlineIds: Set<string>) {
    if (!this.hasLoadedPresence || !this.user) return;

    onlineIds.forEach((userId) => {
      if (userId === this.user?.id) return;
      if (this.previousOnlineUserIds.has(userId)) return;
      this.notifyUserOnline(userId);
    });
  }

  private notifyNewMessages(messages: ChatMessage[]) {
    if (messages.length === 1) {
      const [message] = messages;
      this.toast.info(
        this.language.t("chat.newMessageFrom", {
          name: this.participantName(message.user_id),
          message: this.messagePreview(message.body),
        }),
      );
      return;
    }

    this.toast.info(
      this.language.t("chat.newMessages", {
        count: messages.length,
      }),
    );
  }

  private notifyUserOnline(userId: string) {
    this.toast.success(
      this.language.t("chat.userOnline", {
        name: this.participantName(userId),
      }),
    );
  }

  private applyIncomingUnread(messages: ChatMessage[]) {
    messages.forEach((message) => {
      const senderId = message.user_id;
      const conversationIsOpen =
        this.open && this.selectedParticipantId === senderId;
      if (conversationIsOpen) return;

      this.unreadByUserId.set(senderId, this.unreadFor(senderId) + 1);
    });
    this.updateUnreadTotal();
  }

  private clearUnreadFor(userId: string) {
    this.unreadByUserId.delete(userId);
    this.updateUnreadTotal();
  }

  private updateUnreadTotal() {
    this.unreadCount = Array.from(this.unreadByUserId.values()).reduce(
      (total, count) => total + count,
      0,
    );
  }

  private isConversationMessage(message: ChatMessage, participantId: string) {
    if (!this.user || !this.isVisibleDirectMessage(message)) return false;
    const senderId = message.user_id;
    const recipientId = message.recipient_user_id;

    return (
      (senderId === this.user.id && recipientId === participantId) ||
      (senderId === participantId && recipientId === this.user.id)
    );
  }

  private isVisibleDirectMessage(message: ChatMessage) {
    return (
      Boolean(message.id && message.user_id && message.recipient_user_id) &&
      Boolean(message.body) &&
      !this.isExpired(message)
    );
  }

  private isExpired(message: ChatMessage) {
    if (!message.expires_at) return false;
    return new Date(message.expires_at).getTime() <= Date.now();
  }

  private messagePreview(message: string) {
    const normalized = message.replace(/\s+/g, " ").trim();
    return normalized.length > 80
      ? `${normalized.slice(0, 77).trimEnd()}...`
      : normalized;
  }

  private readChatAvailability() {
    if (!this.user) return true;

    try {
      const storedValue = window.localStorage.getItem(
        `${CHAT_AVAILABILITY_KEY_PREFIX}${this.user.id}`,
      );
      return storedValue !== "offline";
    } catch {
      return true;
    }
  }

  private writeChatAvailability() {
    if (!this.user) return;

    try {
      window.localStorage.setItem(
        `${CHAT_AVAILABILITY_KEY_PREFIX}${this.user.id}`,
        this.chatAvailable ? "online" : "offline",
      );
    } catch {
      // Ignore storage failures; the remote presence still follows this session.
    }
  }

  private isCompactChatViewport() {
    return (
      typeof window !== "undefined" &&
      window.innerWidth < COMPACT_CHAT_BREAKPOINT_PX
    );
  }

  private markView() {
    this.cdr.markForCheck();
  }

  private saveLocalMessage(body: string, recipientId: string) {
    if (!this.user) return;

    const nextMessage: ChatMessage = {
      id: `local-${localId()}`,
      user_id: this.user.id,
      recipient_user_id: recipientId,
      body,
      created_at: new Date().toISOString(),
      expires_at: new Date(Date.now() + MESSAGE_TTL_MS).toISOString(),
    };
    const nextMessages = [...this.readLocalMessages(), nextMessage].slice(
      -MESSAGE_LIMIT,
    );

    window.localStorage.setItem(
      CHAT_LOCAL_MESSAGES_KEY,
      JSON.stringify(nextMessages),
    );
    this.messages = nextMessages.filter((message) =>
      this.isConversationMessage(message, recipientId),
    );
    this.ensureMessageParticipants();
    this.scrollToBottomSoon();
  }

  private readLocalMessages() {
    try {
      const rawMessages = window.localStorage.getItem(CHAT_LOCAL_MESSAGES_KEY);
      const parsedMessages = rawMessages ? JSON.parse(rawMessages) : [];

      return Array.isArray(parsedMessages)
        ? (parsedMessages.filter(
            (message) =>
              message &&
              typeof message.id === "string" &&
              typeof message.user_id === "string" &&
              typeof message.recipient_user_id === "string" &&
              typeof message.body === "string" &&
              typeof message.created_at === "string" &&
              typeof message.expires_at === "string" &&
              !this.isExpired(message as ChatMessage),
          ) as ChatMessage[])
        : [];
    } catch {
      return [];
    }
  }

  private scrollToBottomSoon() {
    window.setTimeout(() => {
      const list = this.messageList?.nativeElement;
      if (!list) return;
      list.scrollTop = list.scrollHeight;
    }, 0);
  }

  buttonClasses = buttonClasses;
  inputClasses = inputClasses;
}
